import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import useLiveMatchAnnouncer from "@/hooks/useLiveMatchAnnouncer";
import {
  LayoutDashboard, Users, ImageIcon, MessagesSquare, FolderArchive,
  User as UserIcon, LogOut, Menu, X, Globe, Home, Calendar, Newspaper, Trophy, UsersRound, Package, BarChart3, Cloud, Handshake, Settings, ArrowUpRight, ChevronLeft, Ticket, ShoppingBag,
} from "lucide-react";

function NavItem({ to, icon: Icon, label, testid, onClick, badge, highlight }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClick}
      data-testid={testid}
      className={({ isActive }) =>
        `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all ${
          isActive
            ? "bg-white text-[hsl(var(--accent))] font-semibold shadow-sm"
            : highlight
              ? "bg-white/15 text-white font-medium ring-1 ring-white/40 hover:bg-white/25"
              : "text-white/80 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[hsl(var(--accent))] text-[10px] font-bold leading-none"
        >
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </NavLink>
  );
}

export default function AppShell({ children, variant = "member", title, actions }) {
  const { user, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [unreadChats, setUnreadChats] = useState(0);
  const [pendingAvailability, setPendingAvailability] = useState(0);

  // PlayHQ Admin card — game day link (admin only)
  const [gamedayUrl, setGamedayUrl] = useState("");

  useEffect(() => {
    if (variant !== "admin" || !isAdmin) return;
    api.get("/settings/gameday-link").then(({ data }) => {
      setGamedayUrl(data.url || "");
    }).catch(() => {});
  }, [variant, isAdmin]);

  // Global live-match announcer — fires a session-scoped toast the first
  // time a Derwent game goes live, no matter which member page they are on.
  useLiveMatchAnnouncer({ enabled: !!user && variant === "member" });

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/chat/unread");
      setUnreadChats(data?.count || 0);
    } catch (_) { /* silent */ }
  }, [user]);

  const refreshPendingAvailability = useCallback(async () => {
    if (!user || isAdmin) return; // admin sidebar doesn't show this
    try {
      const { data } = await api.get("/fixtures/pending-availability");
      setPendingAvailability(data?.count || 0);
    } catch (_) { /* silent */ }
  }, [user, isAdmin]);

  const [attendingEventsCount, setAttendingEventsCount] = useState(0);
  const refreshAttendingEvents = useCallback(async () => {
    if (!user || isAdmin) return;
    try {
      const { data } = await api.get("/events/mine/attending");
      setAttendingEventsCount(Array.isArray(data) ? data.length : 0);
    } catch (_) { /* silent */ }
  }, [user, isAdmin]);

  useEffect(() => {
    refreshUnread();
    refreshPendingAvailability();
    refreshAttendingEvents();
    // Poll every 30s
    const t = setInterval(() => { refreshUnread(); refreshPendingAvailability(); refreshAttendingEvents(); }, 30000);
    return () => clearInterval(t);
  }, [refreshUnread, refreshPendingAvailability, refreshAttendingEvents]);

  // Refresh pending count immediately when the user changes availability anywhere in the app
  useEffect(() => {
    const handler = () => refreshPendingAvailability();
    window.addEventListener("dcc:availability-changed", handler);
    return () => window.removeEventListener("dcc:availability-changed", handler);
  }, [refreshPendingAvailability]);

  // Refresh sidebar attending count whenever the dashboard fires an RSVP change.
  useEffect(() => {
    const handler = () => refreshAttendingEvents();
    window.addEventListener("dcc:events-rsvp-changed", handler);
    return () => window.removeEventListener("dcc:events-rsvp-changed", handler);
  }, [refreshAttendingEvents]);

  // The Chat page itself acks rooms one-by-one as the user opens them, so we
  // just refresh the sidebar badge when the user is in the chat area.
  useEffect(() => {
    if (loc.pathname.startsWith("/m/chat")) {
      const t = setInterval(refreshUnread, 2500);
      return () => clearInterval(t);
    }
  }, [loc.pathname, refreshUnread]);

  // Lock background scrolling when the mobile drawer is open. Prevents the
  // page beneath the overlay from scrolling under the user's finger and
  // avoids the "double scroll" trap common on iOS.
  useEffect(() => {
    if (open) {
      document.body.classList.add("dcc-scroll-lock");
      return () => document.body.classList.remove("dcc-scroll-lock");
    }
  }, [open]);

  // Close the drawer on route change so nav taps don't leave it open
  // behind the new page.
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  /** Scroll to top when clicking the hero header background (not buttons/links). */
  const handleHeroClick = (e) => {
    const tag = e.target.closest("a, button, input, select, textarea, [role='button']");
    if (tag) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const memberNav = [
    { to: "/m", icon: LayoutDashboard, label: "Dashboard", testid: "sidebar-member-dashboard" },
    { to: "/m/fixtures", icon: Trophy, label: "Fixtures", testid: "sidebar-member-fixtures", badge: pendingAvailability, highlight: pendingAvailability > 0 },
    { to: "/m/playhq", icon: Cloud, label: "Results", testid: "sidebar-member-playhq" },
    { to: "/m/stats", icon: BarChart3, label: "Stats", testid: "sidebar-member-stats" },
    { to: "/m/events", icon: Calendar, label: "Events", testid: "sidebar-member-events", badge: attendingEventsCount, highlight: attendingEventsCount > 0 },
    { to: "/m/gallery", icon: ImageIcon, label: "Gallery", testid: "sidebar-member-gallery" },
    { to: "/m/chat", icon: MessagesSquare, label: "Chat", testid: "sidebar-member-chat", badge: unreadChats, highlight: unreadChats > 0 },
    { to: "/m/sponsors", icon: Handshake, label: "Sponsors", testid: "sidebar-member-sponsors" },
    { to: "/m/orders", icon: ShoppingBag, label: "Orders", testid: "sidebar-member-orders" },
    { to: "/m/profile", icon: UserIcon, label: "Profile", testid: "sidebar-member-profile" },
  ];

  // Show the "On Loan" entry only when the member has active loans.
  const activeLoans = Number(user?.active_loan_count || 0);
  const overdueLoans = Number(user?.overdue_loan_count || 0);
  if (activeLoans > 0) {
    memberNav.splice(memberNav.length - 1, 0, {
      to: "/m/loans",
      icon: Package,
      label: "On Loan",
      testid: "sidebar-member-loans",
      badge: activeLoans,
      highlight: overdueLoans > 0,
    });
  }

  const adminNav = [
    { to: "/a", icon: LayoutDashboard, label: "Overview", testid: "sidebar-admin-overview" },
    { to: "/a/system", icon: Settings, label: "System Management", testid: "sidebar-admin-system" },
    { to: "/a/landing", icon: Home, label: "Landing Page", testid: "sidebar-admin-landing" },
    { to: "/a/events", icon: Calendar, label: "Events", testid: "sidebar-admin-events" },
    { to: "/a/news", icon: Newspaper, label: "News", testid: "sidebar-admin-news" },
    { to: "/a/members", icon: Users, label: "Member Management", testid: "sidebar-admin-members" },
    { to: "/a/equipment-loans", icon: Package, label: "Equipment Loans", testid: "sidebar-admin-loans" },
  ];

  // Team Selection is only for the primary admin and admins who also
  // hold a coaching role. Regular committee admins don't need to see it.
  const isCoach = Array.isArray(user?.coaching_roles) && user.coaching_roles.length > 0;
  if (user?.role === "primary_admin" || isCoach) {
    adminNav.push({ to: "/a/teams", icon: UsersRound, label: "Team Selection", testid: "sidebar-admin-teams" });
  }

  adminNav.push(
    { to: "/a/shop", icon: ShoppingBag, label: "Shop", testid: "sidebar-admin-shop" },
    { to: "/a/raffles", icon: Ticket, label: "Raffles", testid: "sidebar-admin-raffles" },
    { to: "/a/playhq", icon: Cloud, label: "PlayHQ", testid: "sidebar-admin-playhq" },
    { to: "/a/gallery", icon: ImageIcon, label: "Gallery", testid: "sidebar-admin-gallery" },
    { to: "/a/chats", icon: MessagesSquare, label: "Chats", testid: "sidebar-admin-chats" },
    { to: "/a/sponsors", icon: Handshake, label: "Sponsors", testid: "sidebar-admin-sponsors" },
  );

  const items = variant === "admin" ? adminNav : memberNav;
  const sectionLabel = variant === "admin" ? "Admin" : "Members";

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] flex">
      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-clip overflow-y-visible">
        {/* Identity hero — displayed on every admin/member page */}
        {variant === "admin" && (
          <div onClick={handleHeroClick} className="w-full bg-[#1a3a7a] text-white relative sticky top-0 z-30 cursor-pointer overflow-hidden" data-testid="admin-hero-card">
            <div className="absolute -right-10 -bottom-20 w-72 h-72 rounded-full bg-[#2a5cc7]/40 blur-3xl pointer-events-none" />
            <div className="absolute left-1/4 -top-16 w-56 h-56 rounded-full bg-[#3b6de0]/20 blur-3xl pointer-events-none" />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 pt-10 sm:pt-12 pb-6 sm:pb-8">
              <div className="flex items-center justify-center gap-4 sm:gap-7">
                <div className="flex flex-col items-center shrink-0" data-testid="admin-hero-identity">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt="Profile photo"
                      className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-4 ring-white/90 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)]"
                      data-testid="admin-hero-avatar"
                    />
                  ) : (
                    <div
                      className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-white/10 flex items-center justify-center text-3xl font-bold ring-4 ring-white/90 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)]"
                      data-testid="admin-hero-avatar-fallback"
                    >
                      {(user?.first_name?.[0] || "A").toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">{user?.role === "primary_admin" ? "Primary Admin" : "Admin"}</div>
                  <div className="mt-1 text-lg sm:text-xl font-semibold" data-testid="admin-hero-name">
                    {user?.first_name} {user?.last_name}
                  </div>
                  {user?.member_id && (
                    <div className="mt-1 text-sm opacity-70 font-mono" data-testid="admin-hero-member-id">
                      ID: {user.member_id}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center justify-start gap-2 flex-wrap">
                {loc.pathname !== "/a" && (
                  <Link to="/a" className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/10" data-testid="hero-admin-overview">
                    <ChevronLeft className="h-3.5 w-3.5" /> Overview
                  </Link>
                )}
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/10" data-testid="hero-public-site">
                  <Globe className="h-3.5 w-3.5" /> Public site
                </Link>
                <Link to="/m" className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/10" data-testid="hero-members-portal">
                  <UserIcon className="h-3.5 w-3.5" /> Members portal
                </Link>
                <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-red-500/20" data-testid="hero-signout">
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>
          </div>
        )}
        {variant === "member" && (
          <div onClick={handleHeroClick} className="w-full bg-[#1a3a7a] text-white relative sticky top-0 z-30 cursor-pointer overflow-hidden" data-testid="member-hero-card">
            <div className="absolute -right-10 -bottom-20 w-72 h-72 rounded-full bg-[#2a5cc7]/40 blur-3xl pointer-events-none" />
            <div className="absolute left-1/4 -top-16 w-56 h-56 rounded-full bg-[#3b6de0]/20 blur-3xl pointer-events-none" />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 pt-10 sm:pt-12 pb-6 sm:pb-8">
              <div className="flex items-center justify-center gap-4 sm:gap-7">
                <div className="flex flex-col items-center shrink-0" data-testid="member-hero-identity">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt="Profile photo"
                      className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-4 ring-white/90 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)]"
                      data-testid="member-hero-avatar"
                    />
                  ) : (
                    <div
                      className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-white/10 flex items-center justify-center text-3xl font-bold ring-4 ring-white/90 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)]"
                      data-testid="member-hero-avatar-fallback"
                    >
                      {(user?.first_name?.[0] || user?.username?.[0] || "M").toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">Membership</div>
                  <div className="mt-1 text-lg sm:text-xl font-semibold" data-testid="member-hero-membership-type">
                    {user?.membership_type || "Member"}
                  </div>
                  {user?.member_id && (
                    <div className="mt-1 text-sm opacity-70 font-mono" data-testid="member-hero-member-id">
                      ID: {user.member_id}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center justify-start gap-2 flex-wrap">
                {loc.pathname !== "/m" && (
                  <Link to="/m" className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/10" data-testid="member-hero-dashboard">
                    <ChevronLeft className="h-3.5 w-3.5" /> Dashboard
                  </Link>
                )}
                <Link to="/m/profile" className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/10" data-testid="member-hero-edit-profile">
                  <ArrowUpRight className="h-3.5 w-3.5" /> Edit profile
                </Link>
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/10" data-testid="hero-public-site-member">
                  <Globe className="h-3.5 w-3.5" /> Public site
                </Link>
                {/* Switch to Admin hidden per user request */}
                <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full border border-white/30 hover:bg-red-500/20" data-testid="hero-signout-member">
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6 md:p-8 lg:p-10">
          {(title || actions) && (
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap" data-testid="page-header">

              {actions && <div className="flex items-center gap-2 ml-auto" data-testid="page-actions">{actions}</div>}
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
